import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Shield, Trash2, ArrowLeft, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { arDigits } from "@/lib/utils";

// PDPL Right-to-Erasure self-service page.
// Public, no auth, rate-limited server-side.
// Emits `noindex` so it doesn't clutter Google results, utility page only.

export default function DataDeletion() {
  const { lang, lh } = useLang();
  const isAR = lang === "ar";
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(isAR ? "أدخل بريداً إلكترونياً صالحاً." : "Please enter a valid email address.");
      return;
    }
    if (!confirmed) {
      toast.error(isAR ? "يجب تأكيد أنك صاحب هذه البيانات." : "Please confirm this data belongs to you.");
      return;
    }
    setBusy(true);
    try {
      if (!codeSent) {
        await api.post("/data-deletion/request", { email, phone: phone || null });
        setCodeSent(true);
        toast.success(isAR ? "أرسلنا رمز تحقّق إلى بريدكم." : "We sent a verification code to your email.");
      } else {
        const r = await api.post("/data-deletion/confirm", { email, code });
        setResult(r.data);
        toast.success(isAR ? "تمّت معالجة طلبك." : "Your request has been processed.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isAR ? "تعذّر إرسال الطلب. حاول لاحقاً." : "Could not submit request. Try again shortly."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{isAR ? "طلب حذف البيانات, بروبيرتك" : "Request Data Deletion, The Propertic"}</title>
        <meta name="robots" content="noindex, follow" />
        <meta name="description" content={isAR
          ? "ممارسة حق الحذف المنصوص عليه في قانون PDPL الإماراتي. تحقّقوا من بريدكم الإلكتروني لحذف بياناتكم من أنظمتنا."
          : "Exercise your PDPL right to erasure. Verify your email to delete your data from The Propertic systems."} />
        <link rel="canonical" href={isAR ? "https://thepropertic.ae/data-deletion?lang=ar" : "https://thepropertic.ae/data-deletion"} />
        <link rel="alternate" hrefLang="en" href="https://thepropertic.ae/data-deletion" />
        <link rel="alternate" hrefLang="ar" href="https://thepropertic.ae/data-deletion?lang=ar" />
        <link rel="alternate" hrefLang="x-default" href="https://thepropertic.ae/data-deletion" />
      </Helmet>

      <section className="pt-32 pb-24" data-testid="data-deletion-page">
        <div className="mx-auto max-w-2xl px-6">
          <Link to={lh("/privacy")} className="inline-flex items-center gap-1.5 text-[11px] font-mono-brand tracking-[0.14em] uppercase text-propertic-muted hover:text-propertic-gold transition-colors mb-6" data-testid="back-to-privacy">
            <ArrowLeft className="w-3 h-3" /> {isAR ? "العودة إلى سياسة الخصوصية" : "Back to Privacy Policy"}
          </Link>

          <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.24em] uppercase mb-3">
            {isAR ? "قانون PDPL الإماراتي · حق الحذف" : "UAE PDPL · Right to Erasure"}
          </p>
          <h1 className="font-editorial text-3xl sm:text-4xl text-propertic-navy leading-tight">
            {isAR ? (
              <>احذف <span className="font-italic-serif text-propertic-gold">كل بياناتك</span> الآن</>
            ) : (
              <>Erase <span className="font-italic-serif text-propertic-gold">all your data</span> now</>
            )}
          </h1>

          <p className="mt-5 text-propertic-ink-2/95 text-[15px] leading-[1.75]">
            {isAR
              ? "بموجب المرسوم بقانون اتحادي رقم 45 لسنة 2021 بشأن حماية البيانات الشخصية (قانون PDPL الإماراتي)، يحقّ لكم طلب حذف كامل بياناتكم من أنظمتنا في أي وقت, دون الحاجة إلى تبرير. أدخلوا بريدكم الإلكتروني وسنرسل رمز تحقّق؛ بعد التأكيد تُحذف السجلّات المرتبطة به نهائياً. السجلّات المرتبطة برقم هاتف فقط يراجعها مستشار مرخّص بعد التحقّق من هويتكم."
              : "Under UAE Federal Decree-Law No. 45 of 2021 on the Protection of Personal Data (PDPL), you have the right to request deletion of your data from our systems at any time, no reason required. Enter your email and we will send a verification code; once confirmed, records linked to it are permanently erased. Records linked only to a phone number are reviewed by a licensed advisor after verifying your identity."}
          </p>

          {result ? (
            <div className="mt-10 rounded-2xl border border-emerald-200 bg-emerald-50 p-6" data-testid="deletion-result">
              <div className="flex items-center gap-3 text-emerald-800">
                <Check className="w-6 h-6" />
                <h2 className="font-editorial text-xl">{isAR ? "تمّت المعالجة" : "Request processed"}</h2>
              </div>
              <p className="mt-3 text-emerald-900/85 text-sm leading-relaxed">
                {isAR
                  ? `اكتملت معالجة الطلب. عدد السجلات المحذوفة المرتبطة ببريدكم: ${arDigits(result.records_processed || 0)}. الاستشارات: ${arDigits(result.leads_deleted || 0)}؛ المحادثات: ${arDigits(result.chat_sessions_deleted || 0)}؛ التقييمات: ${arDigits(result.chat_feedback_deleted || 0)}؛ الاشتراكات: ${arDigits(result.subscriptions_deleted || 0)}. ${result.phone_only_flagged_for_review ? `السجلات المرتبطة بالهاتف فقط والمحالة إلى مستشار للتحقّق: ${arDigits(result.phone_only_flagged_for_review)}. ` : ""}${result.retained_legal_hold ? `السجلات المحتفظ بها لالتزام قانوني: ${arDigits(result.retained_legal_hold)}.` : ""}`
                  : `We deleted ${result.records_processed || 0} record(s) linked to your email (${result.leads_deleted || 0} consultation · ${result.chat_sessions_deleted || 0} chat · ${result.chat_feedback_deleted || 0} feedback · ${result.subscriptions_deleted || 0} subscription). ${result.phone_only_flagged_for_review ? `${result.phone_only_flagged_for_review} phone-only record(s) were sent to an advisor for identity verification. ` : ""}${result.retained_legal_hold ? `${result.retained_legal_hold} record(s) retained under a legal obligation.` : ""}`}
              </p>
              <p className="mt-4 text-emerald-900/60 text-xs">
                {isAR
                  ? "ملاحظة: نحتفظ فقط ببصمة غير قابلة للعكس تُثبت تنفيذ طلبكم."
                  : "Note: we retain only a non-reversible fingerprint proving your request was honoured."}
              </p>
              <div className="mt-5 flex items-center gap-3">
                <Link to={lh("/")} className="btn-gold rounded-full h-10 px-5 text-[11px] font-mono-brand tracking-widest inline-flex items-center" data-testid="deletion-home-cta">
                  {isAR ? "الصفحة الرئيسية" : "Back to home"}
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 rounded-2xl border hairline bg-white p-6 sm:p-8 space-y-6" data-testid="data-deletion-form">
              <label className="block">
                <span className="font-mono-brand text-propertic-muted text-[10px] tracking-[0.18em] uppercase">
                  {isAR ? "البريد الإلكتروني" : "Email address"}
                </span>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" autoFocus disabled={codeSent} dir="ltr" data-testid="deletion-email-input" className="mt-2 w-full bg-white rounded-xl border hairline h-12 px-4 text-propertic-navy text-base focus:border-propertic-gold outline-none" />
              </label>

              {codeSent && (
                <label className="block">
                  <span className="font-mono-brand text-propertic-muted text-[10px] tracking-[0.18em] uppercase">{isAR ? "رمز التحقّق (6 أرقام)" : "Verification code (6 digits)"}</span>
                  <input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660)).replace(/[\u06F0-\u06F9]/g, (d) => String(d.charCodeAt(0) - 0x06F0)).replace(/\D/g, "").slice(0, 6))} required dir="ltr" data-testid="deletion-code-input" className="mt-2 w-full bg-white rounded-xl border hairline h-12 px-4 text-propertic-navy text-base tracking-[0.4em] focus:border-propertic-gold outline-none tabular" />
                </label>
              )}

              <label className="block">
                <span className="font-mono-brand text-propertic-muted text-[10px] tracking-[0.18em] uppercase">
                  {isAR ? "رقم الهاتف (اختياري)" : "Phone number (optional)"}
                </span>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={isAR ? "+971 56 300 3311" : "+971 56 300 3311"}
                  autoComplete="tel"
                  disabled={codeSent}
                  data-testid="deletion-phone-input"
                  dir="ltr"
                  className="mt-2 w-full bg-white rounded-xl border hairline h-12 px-4 text-propertic-navy text-base focus:border-propertic-gold outline-none tabular"
                />
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  data-testid="deletion-confirm-checkbox"
                  className="mt-1 h-4 w-4 accent-propertic-gold shrink-0"
                />
                <span className="text-[13px] text-propertic-ink leading-relaxed">
                  {isAR
                    ? "أؤكّد أنّ هذه البيانات تخصّني، وأطلب حذف البيانات المرتبطة بها من أنظمة بروبيرتك. أفهم أنّ هذا الإجراء نهائي ولا يمكن التراجع عنه."
                    : "I confirm this data belongs to me and request deletion of the data linked to it from The Propertic's systems. I understand this action is permanent and cannot be undone."}
                </span>
              </label>

              <button
                type="submit"
                disabled={busy}
                data-testid="deletion-submit-btn"
                className="btn-primary inline-flex items-center justify-center gap-2 w-full h-12 rounded-full font-mono-brand text-[11px] tracking-[0.16em] uppercase disabled:opacity-60"
              >
                <Trash2 className="w-4 h-4" />
                {busy
                  ? (isAR ? "جارٍ المعالجة…" : "Processing…")
                  : codeSent
                    ? (isAR ? "تأكيد الحذف" : "Confirm deletion")
                    : (isAR ? "أرسل رمز التحقّق" : "Send verification code")}
              </button>

              <div className="pt-4 border-t hairline flex items-start gap-2.5 text-[11px] text-propertic-muted leading-relaxed">
                <Shield className="w-3.5 h-3.5 text-propertic-gold shrink-0 mt-0.5" />
                <p>
                  {isAR
                    ? "لأمنكم: لا يُحذف أي شيء قبل التحقّق من بريدكم. تُقيَّد المحاولات بخمسة طلبات في الساعة، وتنتهي صلاحية الرمز خلال 15 دقيقة."
                    : "For your security: nothing is deleted until your email is verified. Requests are limited to 5 per hour and codes expire after 15 minutes."}
                </p>
              </div>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
